(() => {
  'use strict';

  const CONFIG = {
    debug: true,

    events: {
      start: 'shopify_form_start',
      submitAttempt: 'shopify_form_submit_attempt',
      submit: 'shopify_form_submit',
      success: 'shopify_form_submit_success'
    },

    successTexts: [
      'お問い合わせありがとうございます。',
      'お問い合わせありがとうございます'
    ],

    fieldSelector: [
      'input:not([type="hidden"])',
      'textarea',
      'select'
    ].join(','),

    submitSelector: [
      'button[type="submit"]',
      'input[type="submit"]',
      'button:not([type])'
    ].join(','),

    contextSelectors: [
      'shopify-forms-embed',
      'shopify-forms-popup',
      '[data-shopify-forms]',
      '[data-form-id]',
      '[id*="shopify-form"]',
      '[class*="shopify-form"]',
      '[id*="forms_inline"]'
    ]
  };

  window.dataLayer = window.dataLayer || [];

  const observedRoots = new WeakSet();
  const formStates = new WeakMap();

  let lastSubmission = null;

  function log(...args) {
    if (!CONFIG.debug) {
      return;
    }

    console.log('[Shopify Forms Tracking]', ...args);
  }

  function isElement(node) {
    return Boolean(
      node &&
      node.nodeType === Node.ELEMENT_NODE
    );
  }

  function matches(element, selector) {
    return Boolean(
      isElement(element) &&
      element.matches &&
      element.matches(selector)
    );
  }

  function getEventPath(event) {
    if (event && typeof event.composedPath === 'function') {
      return event.composedPath();
    }

    return event && event.target
      ? [event.target]
      : [];
  }

  function findInEventPath(event, selector) {
    return getEventPath(event).find((node) => {
      return matches(node, selector);
    }) || null;
  }

  function isFormContext(element) {
    if (!isElement(element)) {
      return false;
    }

    const tagName = String(
      element.tagName || ''
    ).toLowerCase();

    if (
      tagName.includes('shopify') &&
      tagName.includes('form')
    ) {
      return true;
    }

    return CONFIG.contextSelectors.some((selector) => {
      try {
        return element.matches(selector);
      } catch (error) {
        return false;
      }
    });
  }

  function findContext(element) {
    let current = element;

    while (current) {
      if (isFormContext(current)) {
        return current;
      }

      if (matches(current, 'form')) {
        return current;
      }

      if (current.parentElement) {
        current = current.parentElement;
        continue;
      }

      if (typeof current.getRootNode === 'function') {
        const root = current.getRootNode();

        if (root && root.host) {
          current = root.host;
          continue;
        }
      }

      break;
    }

    return null;
  }

  function findContextFromEvent(event) {
    const path = getEventPath(event);

    for (const node of path) {
      if (
        isFormContext(node) ||
        matches(node, 'form')
      ) {
        return node;
      }
    }

    return event && event.target
      ? findContext(event.target)
      : null;
  }

  function getState(context) {
    if (!formStates.has(context)) {
      formStates.set(context, {
        started: false,
        submitted: false,
        success: false,
        lastAttemptAt: 0,
        lastSubmitAt: 0
      });
    }

    return formStates.get(context);
  }

  function trimText(value, maxLength = 150) {
    return String(value || '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, maxLength);
  }

  function getAttribute(element, names) {
    if (!element || !element.getAttribute) {
      return '';
    }

    for (const name of names) {
      const value = element.getAttribute(name);

      if (value) {
        return trimText(value);
      }
    }

    return '';
  }

  function getFormMetadata(context) {
    const form = matches(context, 'form')
      ? context
      : context.querySelector?.('form');

    const formId =
      getAttribute(form, [
        'data-form-id',
        'form-id',
        'id',
        'name'
      ]) ||
      getAttribute(context, [
        'data-form-id',
        'form-id',
        'id',
        'name'
      ]) ||
      'shopify_forms';

    const formName =
      getAttribute(form, [
        'data-form-name',
        'aria-label',
        'name'
      ]) ||
      getAttribute(context, [
        'data-form-name',
        'aria-label',
        'title'
      ]) ||
      formId;

    let destination = window.location.pathname;

    if (form && form.action) {
      try {
        destination = new URL(
          form.action,
          window.location.href
        ).pathname;
      } catch (error) {
        destination = window.location.pathname;
      }
    }

    return {
      form_id: formId,
      form_name: formName,
      form_provider: 'shopify_forms',
      form_destination: destination,
      page_path: window.location.pathname
    };
  }

  function pushEvent(eventName, metadata, extra = {}) {
    const payload = {
      event: eventName,
      ...metadata,
      ...extra
    };

    window.dataLayer.push(payload);

    log('dataLayer.push', payload);
  }

  function handleStart(event) {
    const field = findInEventPath(
      event,
      CONFIG.fieldSelector
    );

    if (!field) {
      return;
    }

    const context =
      findContextFromEvent(event) ||
      findContext(field);

    if (!context) {
      return;
    }

    const state = getState(context);

    if (state.started) {
      return;
    }

    state.started = true;

    pushEvent(
      CONFIG.events.start,
      getFormMetadata(context),
      {
        interaction_type: event.type
      }
    );
  }

  function handleSubmitAttempt(event) {
    const button = findInEventPath(
      event,
      CONFIG.submitSelector
    );

    if (!button) {
      return;
    }

    const context =
      findContextFromEvent(event) ||
      findContext(button);

    if (!context) {
      return;
    }

    const state = getState(context);
    const now = Date.now();

    if (now - state.lastAttemptAt < 1000) {
      return;
    }

    state.lastAttemptAt = now;

    const metadata = getFormMetadata(context);

    lastSubmission = {
      context,
      metadata,
      submittedAt: now
    };

    pushEvent(
      CONFIG.events.submitAttempt,
      metadata,
      {
        submit_source: 'submit_button_click'
      }
    );
  }

  function handleSubmit(event) {
    const context =
      findContextFromEvent(event) ||
      findContext(event.target);

    if (!context) {
      return;
    }

    const state = getState(context);
    const now = Date.now();

    if (now - state.lastSubmitAt < 1000) {
      return;
    }

    state.lastSubmitAt = now;
    state.submitted = true;

    const metadata = getFormMetadata(context);

    lastSubmission = {
      context,
      metadata,
      submittedAt: now
    };

    pushEvent(
      CONFIG.events.submit,
      metadata,
      {
        submit_source: 'native_submit'
      }
    );
  }

  function normalizeText(value) {
    return String(value || '')
      .replace(/\s+/g, '')
      .trim();
  }

  function containsSuccessText(element) {
    if (!isElement(element)) {
      return false;
    }

    const elementText = normalizeText(
      element.textContent
    );

    if (!elementText) {
      return false;
    }

    return CONFIG.successTexts.some((text) => {
      return elementText.includes(
        normalizeText(text)
      );
    });
  }

  function isVisible(element) {
    if (!isElement(element)) {
      return false;
    }

    const style = window.getComputedStyle(element);

    if (
      style.display === 'none' ||
      style.visibility === 'hidden'
    ) {
      return false;
    }

    return element.getClientRects().length > 0;
  }

  function handleSuccess() {
    if (!lastSubmission) {
      return;
    }

    const state = getState(
      lastSubmission.context
    );

    if (state.success) {
      return;
    }

    /*
     * 送信操作から2分以内の成功表示だけを対象にする。
     */
    if (
      Date.now() -
        lastSubmission.submittedAt >
      120000
    ) {
      return;
    }

    state.success = true;

    pushEvent(
      CONFIG.events.success,
      lastSubmission.metadata,
      {
        submit_source: 'success_message'
      }
    );
  }

  function checkSuccess(root) {
    const candidates = [];

    if (isElement(root)) {
      candidates.push(root);
    }

    if (root && root.querySelectorAll) {
      candidates.push(
        ...root.querySelectorAll([
          '[role="status"]',
          '[aria-live]',
          '[data-status="success"]',
          '[data-success="true"]',
          '.success-message',
          '[class*="success"]',
          'div',
          'p',
          'span',
          'h1',
          'h2',
          'h3'
        ].join(','))
      );
    }

    const successElement = candidates.find(
      (element) => {
        return (
          isVisible(element) &&
          containsSuccessText(element)
        );
      }
    );

    if (successElement) {
      handleSuccess();
    }
  }

  function scanShadowRoots(node) {
    if (!node) {
      return;
    }

    if (isElement(node) && node.shadowRoot) {
      observeRoot(node.shadowRoot);
    }

    if (!node.querySelectorAll) {
      return;
    }

    node.querySelectorAll('*').forEach(
      (element) => {
        if (element.shadowRoot) {
          observeRoot(element.shadowRoot);
        }
      }
    );
  }

  function observeRoot(root) {
    if (!root || observedRoots.has(root)) {
      return;
    }

    observedRoots.add(root);

    root.addEventListener(
      'focusin',
      handleStart,
      true
    );

    root.addEventListener(
      'input',
      handleStart,
      true
    );

    root.addEventListener(
      'change',
      handleStart,
      true
    );

    root.addEventListener(
      'click',
      handleSubmitAttempt,
      true
    );

    root.addEventListener(
      'submit',
      handleSubmit,
      true
    );

    const observerTarget =
      root.nodeType === Node.DOCUMENT_NODE
        ? root.documentElement
        : root;

    if (observerTarget) {
      const observer = new MutationObserver(
        (mutations) => {
          mutations.forEach((mutation) => {
            mutation.addedNodes.forEach(
              (node) => {
                scanShadowRoots(node);
                checkSuccess(node);
              }
            );
          });

          checkSuccess(root);
        }
      );

      observer.observe(observerTarget, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    scanShadowRoots(root);
    checkSuccess(root);

    log('監視開始', root);
  }

  observeRoot(document);

  /*
   * Web Componentの生成が遅い場合への対策。
   * 最大20秒間、Shadow Rootを再確認する。
   */
  let scans = 0;

  const scanTimer = window.setInterval(
    () => {
      scanShadowRoots(document);

      scans += 1;

      if (scans >= 10) {
        window.clearInterval(scanTimer);
      }
    },
    2000
  );
})();